import { Card, CardContent } from "@/components/ui/card";

interface KpiProps {
  label: string;
  /**
   * `null` — законное значение: показатель неприменим к выбранному разрезу
   * (например, ОСНС у ИП считается только по области, не по району).
   * `formatCurrency` рисует такой случай прочерком, а `0` соврал бы «никого».
   */
  value: string | number | null;
  originalValue?: string;
  delta?: string;
  positive?: boolean;
  none?: boolean;
  hint?: string | number;
  /** Устарело: декоративные sparklines убраны. */
  showSparkline?: boolean;
  trendingUp?: boolean;
  loading?: boolean;
}

const formatCurrency = (value: string | number | undefined | null): string => {
  if (value === undefined || value === null) {
    return "—";
  }

  const stringValue = typeof value === "number" ? value.toString() : value;
  const numericString = stringValue.replace(/[^\d.,]/g, "");
  const normalized = numericString.replace(",", ".");
  const number = parseFloat(normalized);

  if (isNaN(number)) {
    return stringValue;
  }

  return new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(number);
};

/**
 * Карточка метрики планшета: число + текстовый рост/падение.
 * Ложные красные/зелёные «графики» не рисуем — только реальный delta.
 */
export function Kpi({
  label,
  value,
  delta,
  positive,
  none,
  hint,
  loading,
  originalValue,
}: KpiProps) {
  const formattedValue = formatCurrency(value);

  return (
    <Card className="shadow-sm h-full">
      <CardContent className="p-4 flex h-full flex-col gap-3">
        {delta && (
          <div
            className={`inline-flex items-center gap-1 text-xs font-semibold ${
              none
                ? "text-gray-600"
                : positive
                  ? "text-green-600"
                  : "text-red-600"
            }`}
          >
            <span>{delta}</span>
          </div>
        )}

        <div className="dg-num text-2xl ipad:text-[30px] font-bold leading-tight text-[var(--dg-chalk)]">
          {loading ? (
            <div className="h-8 bg-gray-200 rounded animate-pulse" />
          ) : (
            originalValue ?? formattedValue
          )}
        </div>

        <div className="text-xs font-medium text-gray-500 mt-auto">{label}</div>

        {hint != null && hint !== "" && (
          <p className="text-xs text-gray-500">{hint}</p>
        )}
      </CardContent>
    </Card>
  );
}
